import FundOpportunity from '../../../models/FundOpportunity';
import Investor from '../../../models/Investor';

export function fmtCurrency(n: number | null | undefined): string {
  if (n == null) return '—';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}m`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toLocaleString('en-AU')}`;
}

export function noteAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60_000);
  const h = Math.floor(diffMs / 3_600_000);
  const d = Math.floor(diffMs / 86_400_000);
  if (m < 2) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

export function fundStatusLabel(status: FundOpportunity['status']): string {
  return (
    (
      {
        NEW: 'New',
        APPROVED: 'Approved',
        EOI: 'EOI Open',
        APPLICATION_OPEN: 'Application Open',
        UNDER_MANAGEMENT: 'Under Management',
        COMPLETE: 'Complete',
      } as Record<string, string>
    )[status] ?? status
  );
}

export function fundStatusColor(
  status: FundOpportunity['status'],
): 'default' | 'info' | 'warning' | 'success' {
  if (status === 'EOI') return 'info';
  if (status === 'APPLICATION_OPEN') return 'warning';
  return 'default';
}

export function eoiStatusLabel(s: Investor['status']) {
  return (
    (
      {
        CANDIDATE: 'Candidate',
        LEAD: 'Lead',
        OPPORTUNITY: 'Opportunity',
        COMMITTED_INVESTOR: 'Investor',
      } as const
    )[s] ?? s
  );
}

export function eoiStatusColor(s: Investor['status']): 'default' | 'info' | 'warning' | 'success' {
  return (
    (
      {
        CANDIDATE: 'default',
        LEAD: 'info',
        OPPORTUNITY: 'warning',
        COMMITTED_INVESTOR: 'success',
      } as const
    )[s] ?? 'default'
  );
}

export function getInvestorFundId(investor: Investor): string | undefined {
  const fund = investor.fundOpportunity as { id?: string } | undefined;
  return fund?.id ?? investor.fundOpportunityId;
}

export function getActiveEoiInvestors(investments: Investor[]): Investor[] {
  return investments.filter(
    (inv) => inv.eoiSubmitted || inv.status === 'LEAD' || inv.status === 'OPPORTUNITY',
  );
}

export function partitionAvailableFunds(
  investments: Investor[],
  availableFunds: FundOpportunity[],
): {
  investedFundIds: Set<string>;
  newFunds: FundOpportunity[];
} {
  const investedFundIds = new Set(
    investments.map(getInvestorFundId).filter((id): id is string => Boolean(id)),
  );
  const newFunds = availableFunds.filter((f) => !investedFundIds.has(f.id));
  return { investedFundIds, newFunds };
}
